/**
 * SavingsPreviewChip — customer-facing member-savings preview.
 *
 * Reactive to whatever inputs the customer has configured in
 * `<QuoteTool>` on the same page. The parent (`Pricing.jsx`) captures
 * QuoteTool state via `onInputsChange` and forwards it here as
 * `quoteInputs`. Debounced 300ms so rapid input flicks (double-
 * clicking a dog button) don't hammer `/quote`.
 *
 * Fallback: if `quoteInputs` is incomplete (customer hasn't picked
 * all required fields yet), the chip renders using the static sample
 * payload (`SAMPLE_QUOTE_PAYLOAD`). We never HIDE just because inputs
 * are missing — hiding is reserved for the API-failure path.
 *
 * Loading behavior:
 *   - First mount, no data yet:      chip HIDDEN (returns null).
 *   - Post-refresh with data:        chip shows PREVIOUS rows while
 *                                    refetching (no flash-to-empty).
 *   - API error at any point:        chip HIDDEN.
 *
 * ─── HARD COPY RULE (rewritten for derived-percent rendering) ───
 * The rendered percent is DERIVED per-booking from the engine
 * response: `round((baselineTotal - previewTotal) / baselineTotal *
 * 100)`. It is NEVER a hardcoded tier-max literal like `"10%"` or
 * `"17%"`. This matters because the pricing engine clamps the
 * effective discount to the tier's commission when the tier max
 * would push platform take negative — a small-booking case gets a
 * smaller effective discount than the tier badge suggests, and the
 * chip must reflect the real per-booking rate, never the max.
 *
 * Two-layer guard:
 *   1. Runtime derivation from the engine's per-tier `/quote`
 *      response (this file).
 *   2. Static-source scan in `SavingsPreviewChip.test.jsx` (G9) and
 *      `backend/tests/meta/test_pricing_config_integrity.py`
 *      (`test_family_g_savings_chip_no_percent_leak`) — both forbid
 *      hardcoded rate literals in the chip source.
 *
 * The static guards also ban rate-framing English words
 * (`percent`, `rate`, `off`) inside string literals so no future
 * contributor can smuggle in flat marketing language.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

const SAMPLE_QUOTE_PAYLOAD = {
  dog_count: 1,
  frequency: "weekly",
  is_first_booking: false,
};

const REFETCH_DEBOUNCE_MS = 300;

/**
 * Return the effective quote payload for the chip. If the caller
 * supplied `quoteInputs` with BOTH `dog_count` and `frequency`
 * populated, use it; otherwise fall back to the sample.
 * `is_first_booking` is optional and defaults to the sample's value
 * when absent.
 */
function resolvePayload(quoteInputs) {
  const dc = quoteInputs?.dog_count;
  const fq = quoteInputs?.frequency;
  const complete =
    Number.isInteger(dc) && dc >= 1 && typeof fq === "string" && fq.length > 0;
  if (!complete) return { ...SAMPLE_QUOTE_PAYLOAD };
  return {
    dog_count: dc,
    frequency: fq,
    is_first_booking:
      typeof quoteInputs.is_first_booking === "boolean"
        ? quoteInputs.is_first_booking
        : SAMPLE_QUOTE_PAYLOAD.is_first_booking,
  };
}

export default function SavingsPreviewChip({
  testId = "savings-preview-chip",
  quoteInputs = null,
}) {
  // `null` = never resolved (hide). `[]` = resolved but no discount
  // tiers with positive savings (hide). Non-empty array = render rows.
  const [rows, setRows] = useState(null);

  // Payload is stable across renders unless the underlying inputs
  // actually change (compared by JSON serialisation so object-ref
  // churn in the parent doesn't cause unnecessary refetches).
  const payload = useMemo(() => resolvePayload(quoteInputs), [quoteInputs]);
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);

  // Stale-response guard — increment on every effect entry so a slow
  // response for the previous payload can't clobber a newer answer.
  const reqIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      const myReqId = ++reqIdRef.current;

      (async () => {
        try {
          // 1) Live tier list — source of truth for tier NAMES only.
          //    The tier's advertised `discount_pct` is NOT rendered
          //    directly; it's only used to request the per-tier
          //    preview quote. The rendered percent comes strictly
          //    from the delta between baseline and preview totals.
          const { data: plans } = await api.get("/public/customer-plans");
          if (reqIdRef.current !== myReqId) return;
          const discountTiers = (plans || []).filter(
            (p) => (p.discount_pct || 0) > 0,
          );
          if (discountTiers.length === 0) {
            setRows([]);
            return;
          }

          // 2) Baseline quote (no discount).
          const { data: baseline } = await api.post("/quote", { ...payload });
          if (reqIdRef.current !== myReqId) return;
          const baselineTotal = baseline?.total ?? null;
          if (baselineTotal == null || baselineTotal <= 0) {
            throw new Error("baseline quote missing or non-positive total");
          }

          // 3) One preview quote per positive-discount tier. Derive
          //    the effective per-booking percent from the dollar
          //    delta — the engine has already applied the commission-
          //    cap clamp inside the preview total, so this ratio
          //    truthfully reflects the discount the customer would
          //    receive on THIS exact booking.
          const previews = await Promise.all(
            discountTiers.map((tier) =>
              api
                .post("/quote", {
                  ...payload,
                  member_discount_pct: tier.discount_pct,
                })
                .then(({ data }) => {
                  const previewTotal = data?.total ?? baselineTotal;
                  const savingsDollars = Math.max(
                    0,
                    baselineTotal - previewTotal,
                  );
                  const savingsPct = Math.round(
                    (savingsDollars / baselineTotal) * 100,
                  );
                  return {
                    name: tier.name,
                    savingsDollars,
                    savingsPct,
                  };
                })
                .catch(() => null),
            ),
          );
          if (reqIdRef.current !== myReqId) return;
          const settled = previews.filter(
            (p) => p !== null && p.savingsPct > 0,
          );
          setRows(settled);
        } catch {
          // Silent hide on hard failure.
          if (reqIdRef.current !== myReqId) return;
          setRows([]);
        }
      })();
    }, REFETCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey]);

  if (!rows || rows.length === 0) return null;

  return (
    <div
      className="pp-wrap flex flex-col items-center gap-2 pb-10 md:pb-12"
      data-testid={testId}
    >
      {rows.map((row) => (
        <span
          key={row.name}
          className="inline-flex items-center rounded-full bg-brand-cream-tint px-4 py-1.5 text-sm font-semibold text-pp-slate ring-1 ring-brand-orange/25"
          data-testid={`${testId}-${row.name.toLowerCase()}`}
        >
          {`${row.name} members would save ${row.savingsPct}% on this booking`}
        </span>
      ))}
    </div>
  );
}
